import "./editor-styles.css";

import { Color } from "@tiptap/extension-color";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import Placeholder from "@tiptap/extension-placeholder";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextStyle from "@tiptap/extension-text-style";
import { EditorProvider, useCurrentEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { FileText, Maximize2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@repo/ui";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui";
import { FormatBubble, LinkBubble, TableBubble } from "./bubble-menus";
import { ListDepthClasses } from "./list-depth-classes";
import { SlashCommand } from "./slash-command";

const extensions = [
  // Kept so existing coloured text survives a round trip; there is no colour UI.
  Color.configure({ types: [TextStyle.name, ListItem.name] }),
  TextStyle.configure(),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
    },
  }),
  Table.configure({
    resizable: true,
  }),
  TableRow,
  TableCell,
  TableHeader,
  Link.configure({
    defaultProtocol: "https",
    openOnClick: false,
    autolink: true,
    linkOnPaste: true,
  }),
  Placeholder.configure({
    includeChildren: true,
    placeholder: ({ editor, node, pos }) => {
      if (node.type.name === "heading") return `Heading ${node.attrs.level}`;
      const parent = editor.state.doc.resolve(pos).parent.type.name;
      if (parent === "listItem") return "List";
      if (parent === "blockquote") return "Quote";
      return "Type / for commands";
    },
  }),
  SlashCommand,
  ListDepthClasses,
];

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  expandable?: boolean;
  expandTitle?: string;
  /** Controls the full-screen dialog; the caller then renders its own expand trigger. */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const ContentSetter = ({
  content,
  isUserInputRef,
}: {
  content: string;
  isUserInputRef: React.MutableRefObject<boolean>;
}) => {
  const { editor } = useCurrentEditor();
  // Normalize content to handle null/undefined/empty string consistently
  const normalizeContent = (val: string | null | undefined): string => {
    return val ?? "";
  };

  // Update editor content when content prop changes externally (not from user input)
  useEffect(() => {
    if (!editor) return;

    // Skip update if this change came from user input
    if (isUserInputRef.current) {
      return;
    }

    // Skip update if editor is focused (user is actively typing)
    if (editor.isFocused) {
      return;
    }

    const normalizedContent = normalizeContent(content);
    const currentContent = editor.getHTML();
    const normalizedCurrentContent = normalizeContent(currentContent);

    // Only update if the content actually changed
    if (normalizedContent !== normalizedCurrentContent) {
      editor.commands.setContent(normalizedContent);
    }
  }, [editor, content, isUserInputRef]);

  return null;
};

const TiptapInstance = ({
  content,
  onChange,
  onExpandClick,
  onSubmit,
  fullscreen,
}: {
  content: string;
  onChange: (content: string) => void;
  onExpandClick?: () => void;
  /** ⌘↵; takes precedence over the hard break StarterKit binds to it. */
  onSubmit?: () => void;
  fullscreen?: boolean;
}) => {
  // Track if we're currently updating from user input to prevent circular updates
  const isUserInputRef = useRef<boolean>(false);
  const [linkRequest, setLinkRequest] = useState(0);

  const handleUpdate = ({ editor }: { editor: Editor }) => {
    // Mark that we're updating from user input
    isUserInputRef.current = true;
    onChange(editor.getHTML());
    // Reset flag after a short delay to allow the content prop to update
    setTimeout(() => {
      isUserInputRef.current = false;
    }, 0);
  };

  return (
    <div
      className={cn(
        "group relative flex min-h-0 flex-1 flex-col",
        onExpandClick && "rich-text-editor-expandable",
      )}
    >
      <EditorProvider
        extensions={extensions}
        content={content}
        onUpdate={handleUpdate}
        immediatelyRender={false}
        editorProps={{
          handleKeyDown: (_view, event) => {
            const mod = event.metaKey || event.ctrlKey;
            if (mod && event.key === "Enter" && onSubmit) {
              onSubmit();
              return true;
            }
            if (mod && event.key.toLowerCase() === "k") {
              setLinkRequest((n) => n + 1);
              return true;
            }
            return false;
          },
        }}
        editorContainerProps={{
          className: fullscreen
            ? "rich-text-editor-fullscreen relative flex flex-1 flex-col min-h-0"
            : "relative",
        }}
      >
        <FormatBubble linkRequest={linkRequest} />
        <LinkBubble linkRequest={linkRequest} />
        <TableBubble />
        <ContentSetter content={content} isUserInputRef={isUserInputRef} />
      </EditorProvider>
      {onExpandClick && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Expand"
          // Clears the 10px scrollbar; opaque so scrolled text doesn't show through.
          className="absolute right-3 top-3 h-7 w-7 bg-background text-muted-foreground opacity-0 transition-opacity focus-visible:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100"
          onClick={onExpandClick}
        >
          <Maximize2 size={14} />
        </Button>
      )}
    </div>
  );
};

export const RichTextEditor = ({
  content,
  onChange,
  expandable = false,
  expandTitle = "Edit Content",
  expanded,
  onExpandedChange,
}: RichTextEditorProps) => {
  const [localExpanded, setLocalExpanded] = useState(false);
  const isControlled = expanded !== undefined;
  const isExpanded = isControlled ? expanded : localExpanded;
  const setIsExpanded = isControlled ? (v: boolean) => onExpandedChange?.(v) : setLocalExpanded;

  return (
    <>
      <TiptapInstance
        content={content}
        onChange={onChange}
        onExpandClick={expandable && !isControlled ? () => setIsExpanded(true) : undefined}
      />
      {expandable && (
        <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
          <DialogContent
            aria-describedby={undefined}
            className="flex h-[85vh] max-w-[860px] flex-col gap-0 overflow-hidden p-0 rounded-2xl"
          >
            <div className="flex items-center px-5 pb-3 pt-3">
              <DialogTitle className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                <FileText size={12} />
                {expandTitle}
              </DialogTitle>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">
              <TiptapInstance
                content={content}
                onChange={onChange}
                onSubmit={() => setIsExpanded(false)}
                fullscreen
              />
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-3">
              <kbd className="font-sans text-xs text-muted-foreground">⌘↵</kbd>
              <Button type="button" size="sm" onClick={() => setIsExpanded(false)}>
                Done
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
